import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LeaveIcon from "@mui/icons-material/ExitToApp";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { Breadcrumbs } from "components/common/Breadcrumbs";
import { Breadcrumb } from "components/common/Breadcrumbs/types";
import { FormDialog, selectOptionSchema } from "components/common/FormDialog";
import {
  FIELD_TYPES,
  FormDialogProps,
  SelectOption
} from "components/common/FormDialog/types";
import { Head } from "components/common/Head";
import { Loader } from "components/common/Loader";
import { Table } from "components/common/Table";
import {
  TableColumn,
  TableRowActionsMenuItem
} from "components/common/Table/types";
import { useMount } from "hooks/useMount";
import { usePrevious } from "hooks/usePrevious";
import { useUnmount } from "hooks/useUnmount";
import { enterprisesActions } from "modules/enterprises/slices";
import {
  groupSelector,
  isGroupDeletingSelector,
  isGroupInvitationDeletingSelector,
  isGroupLeavingSelector,
  isGroupUpdatingSelector,
  isUserInvitingToGroupSelector,
  isUserRemovingFromGroupSelector,
  organizationSelector,
  tableGroupUsersSelector
} from "modules/enterprises/selectors";
import { ROLES, TableUser } from "modules/enterprises/types";
import { pollingActions } from "modules/polling/slices";
import { createPolling } from "modules/polling/utils";
import { projectsActions } from "modules/projects/slices";
import {
  isGroupFromProjectRemovingSelector,
  isGroupToProjectAssigningSelector,
  organizationProjectsSelector
} from "modules/projects/selectors";
import { TableProject } from "modules/projects/types";
import { FC, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { getParentPath } from "utils/getParentPath";
import { getProjectRegion } from "utils/getProjectRegion";
import { getSelectOption } from "utils/getSelectOption";
import { validateName } from "utils/validateName";
import { ref, string } from "yup";
import {
  ENTITY_NAME_LENGTH,
  ERROR_MESSAGES,
  REGEX,
  ROUTES
} from "../../constants";
import * as s from "./styles";
import { DIALOG_TYPES } from "./types";
import { userSelector } from "modules/auth/selectors";

const POLL_ID_PREFIX = "GROUP";

const POLL_IDS = {
  group: "GROUP",
  groupUsers: "GROUP_USERS",
  groupInvitations: "GROUP_INVITATIONS",
  organizationProjects: "ORGANIZATION_PROJECTS"
};

const userTableColumns: TableColumn<TableUser>[] = [
  { key: "fullName", label: "Full Name" },
  { key: "id", label: "ID" },
  { key: "formattedEmail", label: "E-mail" }
];

const projectTableColumns: TableColumn<TableProject>[] = [
  { key: "name", label: "Project Name" },
  { key: "id", label: "ID" },
  { key: "region", label: "Region" }
];

export const Group: FC = () => {
  const dispatch = useDispatch();
  const matchParams = useParams<{
    organizationId: string;
    groupId: string;
  }>();
  const history = useNavigate();
  const currentUser = useSelector(userSelector);
  const organization = useSelector(organizationSelector);
  const group = useSelector(groupSelector);
  const users = useSelector(tableGroupUsersSelector);
  const isUserInviting = useSelector(isUserInvitingToGroupSelector);
  const isInvitationDeleting = useSelector(isGroupInvitationDeletingSelector);
  const isInvitationOperationInProgress =
    isUserInviting || isInvitationDeleting;
  const previousIsInvitationOperationInProgress = usePrevious(
    isInvitationOperationInProgress
  );
  const isUserRemoving = useSelector(isUserRemovingFromGroupSelector);
  const organizationProjects = useSelector(organizationProjectsSelector);
  const isGroupUpdating = useSelector(isGroupUpdatingSelector);
  const isGroupDeleting = useSelector(isGroupDeletingSelector);
  const isGroupLeaving = useSelector(isGroupLeavingSelector);
  const isGroupAssigning = useSelector(isGroupToProjectAssigningSelector);
  const isGroupRemoving = useSelector(isGroupFromProjectRemovingSelector);
  const isGroupOperationInProgress =
    isGroupAssigning ||
    isGroupRemoving ||
    isGroupUpdating ||
    isGroupDeleting ||
    isGroupLeaving;
  const previousIsUserRemoving = usePrevious(isUserRemoving);
  const previousIsGroupOperationInProgress = usePrevious(
    isGroupOperationInProgress
  );
  const [dialog, setDialog] = useState<{
    isOpened: boolean;
    type: DIALOG_TYPES;
  }>({ type: DIALOG_TYPES.ADD_USER, isOpened: false });
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const isUserAdmin = [ROLES.OWNER, ROLES.ADMIN].includes(
    organization?.role as ROLES
  );
  const prevIsUserAdmin = usePrevious(isUserAdmin);

  const handleCloseDialog = useCallback(() => {
    setDialog({
      ...dialog,
      isOpened: false
    });
    setSelectedItemId(null);
  }, [dialog]);

  const handleEditGroupButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.EDIT_GROUP,
      isOpened: true
    });
  }, []);

  const handleDeleteGroupButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.DELETE_GROUP,
      isOpened: true
    });
  }, []);

  const handleLeaveGroupButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.LEAVE_GROUP,
      isOpened: true
    });
  }, []);

  const breadcrumbs: Breadcrumb[] = [
    { text: "Organizations", url: ROUTES.ORGANIZATIONS },
    {
      text: organization?.name || "",
      url: generatePath(ROUTES.ORGANIZATION, {
        organizationId: matchParams.organizationId
      })
    },
    {
      text: "Groups",
      url: generatePath(ROUTES.GROUPS, {
        organizationId: matchParams.organizationId
      })
    },
    {
      text: group?.name || "",
      url: generatePath(ROUTES.GROUP, {
        organizationId: matchParams.organizationId,
        groupId: matchParams.groupId
      })
    }
  ];

  const handleAddUserButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.ADD_USER,
      isOpened: true
    });
  }, []);

  const handleRemoveUserMenuItemClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.REMOVE_USER,
      isOpened: true
    });
  }, []);

  const handleResendInvitationMenuItemClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.RESEND_INVITATION,
      isOpened: true
    });
  }, []);

  const handleDeleteInvitationMenuItemClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.DELETE_INVITATION,
      isOpened: true
    });
  }, []);

  const handleAssignGroupButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.ASSIGN_GROUP,
      isOpened: true
    });
  }, []);

  const handleRemoveGroupMenuItemClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.REMOVE_GROUP,
      isOpened: true
    });
  }, []);

  const userTableActions: TableRowActionsMenuItem<TableUser>[] = [
    {
      label: "Remove",
      isDisabled: (user) => Boolean(user.invitationId),
      handler: handleRemoveUserMenuItemClick
    },
    {
      label: "Resend invitation",
      isDisabled: (user) => !user.invitationId,
      handler: handleResendInvitationMenuItemClick
    },
    {
      label: "Delete invitation",
      isDisabled: (user) => !user.invitationId,
      handler: handleDeleteInvitationMenuItemClick
    }
  ];

  const projectTableActions: TableRowActionsMenuItem<TableProject>[] = [
    {
      label: "Remove",
      handler: handleRemoveGroupMenuItemClick,
      isDisabled: () => !isUserAdmin
    }
  ];

  const generateProjectTableItemURL = useCallback(
    (id: string) => {
      if (group) {
        return generatePath(ROUTES.PROJECT, {
          organizationId: matchParams.organizationId,
          regionId: getProjectRegion(group?.projects, id),
          projectId: id
        });
      }
    },
    [matchParams.organizationId, group]
  );

  useMount(() => {
    dispatch(
      enterprisesActions.getOrganizationRequest({
        id: matchParams.organizationId!
      })
    );
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.group}`,
        actionCreator: enterprisesActions.getGroupRequest,
        params: {
          organizationId: matchParams.organizationId!,
          groupId: matchParams.groupId!
        }
      })
    );
    if (isUserAdmin) {
      dispatch(
        createPolling({
          id: `${POLL_ID_PREFIX}/${POLL_IDS.groupUsers}`,
          actionCreator: enterprisesActions.getGroupUsersRequest,
          params: {
            organizationId: matchParams.organizationId!,
            groupId: matchParams.groupId!
          }
        })
      );
    }
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.groupInvitations}`,
        actionCreator: enterprisesActions.getGroupInvitationsRequest,
        params: {
          organizationId: matchParams.organizationId!,
          groupId: matchParams.groupId!
        }
      })
    );
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.organizationProjects}`,
        actionCreator: projectsActions.getOrganizationProjectsRequest,
        params: {
          organizationId: matchParams.organizationId!
        }
      })
    );
  });

  useUnmount(() => {
    Object.values(POLL_IDS).forEach((id) => {
      dispatch(
        pollingActions.stopPolling({
          id: `${POLL_ID_PREFIX}/${id}`
        })
      );
    });
    dispatch(enterprisesActions.clear());
    dispatch(projectsActions.clear());
  });

  useEffect(() => {
    if ((previousIsUserRemoving && !isUserRemoving) || isUserAdmin) {
      dispatch(
        enterprisesActions.getGroupUsersRequest({
          organizationId: matchParams.organizationId!,
          groupId: matchParams.groupId!
        })
      );
    }
  }, [
    previousIsUserRemoving,
    isUserRemoving,
    dispatch,
    matchParams.organizationId,
    matchParams.groupId,
    prevIsUserAdmin,
    isUserAdmin
  ]);

  useEffect(() => {
    if (
      previousIsInvitationOperationInProgress &&
      !isInvitationOperationInProgress
    ) {
      dispatch(
        enterprisesActions.getGroupInvitationsRequest({
          organizationId: matchParams.organizationId!,
          groupId: matchParams.groupId!
        })
      );
    }
  }, [
    previousIsInvitationOperationInProgress,
    isInvitationOperationInProgress,
    dispatch,
    matchParams.organizationId,
    matchParams.groupId
  ]);

  useEffect(() => {
    if (previousIsGroupOperationInProgress && !isGroupOperationInProgress) {
      if (!group) {
        history(getParentPath(location.pathname));
      } else {
        dispatch(
          enterprisesActions.getGroupRequest({
            organizationId: matchParams.organizationId!,
            groupId: matchParams.groupId!
          })
        );
      }
    }
  }, [
    group,
    history,
    previousIsGroupOperationInProgress,
    isGroupOperationInProgress,
    dispatch,
    matchParams.organizationId,
    matchParams.groupId
  ]);

  const handleConfirmAddUser = useCallback(
    (data: { email: string }) => {
      dispatch(
        enterprisesActions.inviteUserToGroupRequest({
          organizationId: matchParams.organizationId!,
          groupId: matchParams.groupId!,
          email: data.email
        })
      );
      handleCloseDialog();
    },
    [
      dispatch,
      matchParams.organizationId,
      matchParams.groupId,
      handleCloseDialog
    ]
  );

  const handleConfirmRemoveUser = useCallback(() => {
    if (selectedItemId) {
      dispatch(
        enterprisesActions.removeUserFromGroupRequest({
          organizationId: matchParams.organizationId!,
          groupId: matchParams.groupId!,
          userId: selectedItemId
        })
      );
    }
    handleCloseDialog();
  }, [
    selectedItemId,
    dispatch,
    matchParams.organizationId,
    matchParams.groupId,
    handleCloseDialog
  ]);

  const handleConfirmResendInvitation = useCallback(() => {
    if (selectedItemId) {
      const userToResend = users?.find((user) => user.id === selectedItemId);
      if (userToResend) {
        dispatch(
          enterprisesActions.inviteUserToGroupRequest({
            organizationId: matchParams.organizationId!,
            groupId: matchParams.groupId!,
            email: userToResend.email
          })
        );
      }
    }
    handleCloseDialog();
  }, [
    selectedItemId,
    dispatch,
    matchParams.organizationId,
    matchParams.groupId,
    handleCloseDialog,
    users
  ]);

  const handleConfirmDeleteInvitation = useCallback(() => {
    if (selectedItemId) {
      const userToResend = users?.find((user) => user.id === selectedItemId);
      if (userToResend?.invitationId) {
        dispatch(
          enterprisesActions.deleteGroupInvitationRequest({
            organizationId: matchParams.organizationId!,
            groupId: matchParams.groupId!,
            invitationId: userToResend.invitationId
          })
        );
      }
    }
    handleCloseDialog();
  }, [
    selectedItemId,
    dispatch,
    matchParams.organizationId,
    matchParams.groupId,
    handleCloseDialog,
    users
  ]);

  const handleConfirmAssignGroup = useCallback(
    (data: { project: SelectOption }) => {
      if (organizationProjects) {
        const regionId = getProjectRegion(
          organizationProjects,
          data.project.value
        );
        if (regionId) {
          dispatch(
            projectsActions.assignGroupToProjectRequest({
              projectId: data.project.value,
              regionId,
              organizationId: matchParams.organizationId!,
              groupId: matchParams.groupId!
            })
          );
        }
      }
      handleCloseDialog();
    },
    [
      dispatch,
      organizationProjects,
      matchParams.organizationId,
      matchParams.groupId,
      handleCloseDialog
    ]
  );

  const handleConfirmRemoveGroup = useCallback(() => {
    if (group && selectedItemId) {
      const regionId = getProjectRegion(group.projects, selectedItemId);
      if (regionId) {
        dispatch(
          projectsActions.removeGroupFromProjectRequest({
            projectId: selectedItemId,
            regionId,
            organizationId: matchParams.organizationId!,
            groupId: matchParams.groupId!
          })
        );
      }
    }
    handleCloseDialog();
  }, [
    dispatch,
    group,
    selectedItemId,
    matchParams.organizationId,
    matchParams.groupId,
    handleCloseDialog
  ]);

  const handleConfirmLeaveGroup = useCallback(() => {
    dispatch(
      enterprisesActions.leaveGroupRequest({
        organizationId: matchParams.organizationId!,
        id: matchParams.groupId!
      })
    );
    handleCloseDialog();
  }, [
    dispatch,
    handleCloseDialog,
    matchParams.organizationId,
    matchParams.groupId
  ]);

  const handleConfirmEditGroup = useCallback(
    (data: { name: string }) => {
      dispatch(
        enterprisesActions.updateGroupRequest({
          organizationId: matchParams.organizationId!,
          id: matchParams.groupId!,
          data
        })
      );
      handleCloseDialog();
    },
    [
      dispatch,
      handleCloseDialog,
      matchParams.organizationId,
      matchParams.groupId
    ]
  );

  const handleConfirmDeleteGroup = useCallback(() => {
    dispatch(
      enterprisesActions.deleteGroupRequest({
        organizationId: matchParams.organizationId!,
        id: matchParams.groupId!
      })
    );
    handleCloseDialog();
  }, [
    dispatch,
    handleCloseDialog,
    matchParams.organizationId,
    matchParams.groupId
  ]);

  const previousSelectedItemId = usePrevious(selectedItemId);
  const currentItemId = selectedItemId
    ? selectedItemId
    : previousSelectedItemId;
  const currentProjectName = group?.projects.find(
    (groupProject) => groupProject.id === currentItemId
  )?.name;
  const currentUserName = users?.find(
    (user) => user.id === currentItemId
  )?.email;

  const [isRegisterNewUserDialogOpened, setIsRegisterNewUserDialogOpened] =
    useState(false);

  const handleRegisterNewUser = useCallback(() => {
    setIsRegisterNewUserDialogOpened(true);
  }, []);

  const handleCloseRegisterNewUserDialog = useCallback(() => {
    setIsRegisterNewUserDialogOpened(false);
  }, []);

  const handleConfirmRegisterNewUser = useCallback(
    (data: {
      email_new_user: string;
      first_name: string;
      last_name: string;
      // email_verified: boolean;
      // is_otp_required: boolean;
      // creds_temporary: boolean;
      password: string;
      country: string;
      locality: string;
      mobile: string;
      postal_code: string;
      region: string;
      street: string;
      company_name?: string;
      tax_number?: string;
    }) => {
      dispatch(
        enterprisesActions.registerNewUserRequest({
          data: {
            email: data.email_new_user,
            // email_verified: data.email_verified,
            // is_otp_required: data.is_otp_required,
            // creds_temporary: data.creds_temporary,
            first_name: data.first_name,
            last_name: data.last_name,
            creds_password: data.password,
            attributes: {
              country: data.country,
              locality: data.locality,
              mobile: data.mobile,
              postal_code: data.postal_code,
              region: data.region,
              street: data.street,
              company_name: data.company_name,
              tax_number: data.tax_number,
              customer_id: currentUser?.attributes?.["customer_id"]?.[0] || "",
              reseller: currentUser?.attributes?.["reseller"]?.[0] || ""
            }
          }
        })
      );
      handleCloseRegisterNewUserDialog();
    },
    [dispatch, handleCloseRegisterNewUserDialog, currentUser]
  );

  const dialogProps: {
    [key in DIALOG_TYPES]: Omit<FormDialogProps, "isOpened" | "onCancel">;
  } = {
    [DIALOG_TYPES.ADD_USER]: {
      onConfirm: handleConfirmAddUser,
      title: "Add Member",
      confirmButtonLabel: "Send invitation",
      fields: [
        {
          name: "email",
          type: FIELD_TYPES.TEXT,
          label: "E-mail address",
          rules: string()
            .required()
            .matches(REGEX.EMAIL_ADDRESS, ERROR_MESSAGES.EMAIL_ADDRESS)
        },
        {
          name: "info",
          type: FIELD_TYPES.LABEL,
          label: `\nℹ️ Only already registered users can be assigned as members to the selected projects.\n\n`
        },
        {
          name: "register_new",
          type: FIELD_TYPES.CHECKBOX,
          defaultValue: false,
          fullWidth: true,
          onCreate: handleRegisterNewUser,
          label: "Register new user"
        }
      ]
    },
    [DIALOG_TYPES.REMOVE_USER]: {
      onConfirm: handleConfirmRemoveUser,
      title: `Are you sure you want to remove "${
        currentUserName ?? "selected user"
      }" from the group?`,
      confirmButtonLabel: "Remove"
    },
    [DIALOG_TYPES.RESEND_INVITATION]: {
      onConfirm: handleConfirmResendInvitation,
      title: `Are you sure you want to resend invitation for "${
        currentUserName ?? "selected user"
      }"?`,
      confirmButtonLabel: "Resend"
    },
    [DIALOG_TYPES.DELETE_INVITATION]: {
      onConfirm: handleConfirmDeleteInvitation,
      title: `Are you sure you want to delete invitation for "${
        currentUserName ?? "selected user"
      }"?`,
      confirmButtonLabel: "Delete"
    },
    [DIALOG_TYPES.ASSIGN_GROUP]: {
      onConfirm: handleConfirmAssignGroup,
      title: "Assign to the project",
      confirmButtonLabel: "Assign",
      fields: [
        {
          name: "project",
          type: FIELD_TYPES.SELECT,
          label: "Project",
          options: organizationProjects
            ?.filter(
              (organizationProject) =>
                !group?.projects
                  .map((groupProject) => groupProject.id)
                  .includes(organizationProject.id)
            )
            .map((project) => getSelectOption(project, "name", "id")),
          rules: selectOptionSchema
        }
      ]
    },
    [DIALOG_TYPES.REMOVE_GROUP]: {
      onConfirm: handleConfirmRemoveGroup,
      title: `Are you sure you want to remove "${
        currentProjectName ?? "selected"
      }" project from the group?`,
      confirmButtonLabel: "Remove"
    },
    [DIALOG_TYPES.LEAVE_GROUP]: {
      onConfirm: handleConfirmLeaveGroup,
      title: `Are you sure you want to leave "${
        group?.name ?? "selected"
      }"  group?`,
      confirmButtonLabel: "Leave"
    },
    [DIALOG_TYPES.EDIT_GROUP]: {
      onConfirm: handleConfirmEditGroup,
      title: "Edit group",
      confirmButtonLabel: "Save",
      fields: [
        {
          name: "name",
          type: FIELD_TYPES.TEXT,
          label: "Name",
          defaultValue: group?.name || "",
          rules: string()
            .required()
            .test({
              name: "validateName",
              test: validateName(ENTITY_NAME_LENGTH)
            })
            .matches(REGEX.GROUP_NAME, ERROR_MESSAGES.GROUP_NAME)
        }
      ]
    },
    [DIALOG_TYPES.DELETE_GROUP]: {
      onConfirm: handleConfirmDeleteGroup,
      title: `Are you sure you want to delete "${
        group?.name ?? "selected"
      }" group?`,
      confirmButtonLabel: "Delete"
    }
  };

  const title = group?.name;

  return (
    <>
      <Head title={title} />
      {group ? (
        <>
          {organization && <Breadcrumbs breadcrumbs={breadcrumbs} />}
          <s.SummaryContainer>
            <s.SummaryColumn>
              <s.Title variant={"h4"} component={"h2"}>
                {title}
              </s.Title>
              <s.SummaryRow>
                <s.DetailsTitle>ID: </s.DetailsTitle>
                <s.DetailsInfoColored>{group.id}</s.DetailsInfoColored>
              </s.SummaryRow>
            </s.SummaryColumn>
            <s.ActionsContainer>
              <Tooltip title={"Edit"} arrow>
                <span>
                  <IconButton
                    onClick={handleEditGroupButtonClick}
                    disabled={!isUserAdmin}
                    color={"inherit"}
                    // title={"Edit"}
                  >
                    <EditIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={"Delete"} arrow>
                <span>
                  <IconButton
                    onClick={handleDeleteGroupButtonClick}
                    disabled={!isUserAdmin}
                    color={"inherit"}
                    // title={"Delete"}
                  >
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={"Leave"} arrow>
                <span>
                  <IconButton
                    onClick={handleLeaveGroupButtonClick}
                    disabled={isUserAdmin}
                    color={"inherit"}
                    // title={"Leave"}
                  >
                    <LeaveIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </s.ActionsContainer>
          </s.SummaryContainer>
          <Table<TableProject>
            isSearchEnabled={true}
            isSortingEnabled={true}
            rows={group?.projects || []}
            columns={projectTableColumns}
            actions={projectTableActions}
            itemLink={{
              column: "name",
              getURL: generateProjectTableItemURL
            }}
            toolbarItems={
              isUserAdmin ? (
                <Button
                  onClick={handleAssignGroupButtonClick}
                  variant={"contained"}
                  disabled={!organizationProjects || !isUserAdmin}
                >
                  Assign project
                </Button>
              ) : (
                <s.Description2>Projects assigned to the Group:</s.Description2>
              )
            }
            isLoading={!group}
          />
          {isUserAdmin && (
            <>
              <s.Description>
                The projects above are accessible to the following users:
              </s.Description>
              <Table<TableUser>
                isSearchEnabled={true}
                isSortingEnabled={true}
                rows={users || []}
                columns={userTableColumns}
                actions={userTableActions}
                toolbarItems={
                  <Button
                    onClick={handleAddUserButtonClick}
                    variant={"contained"}
                    disabled={!currentUser}
                  >
                    Add member
                  </Button>
                }
                isLoading={!users || !isUserAdmin}
              />
            </>
          )}
        </>
      ) : (
        <Loader text={"Loading data..."} />
      )}
      <FormDialog
        isOpened={dialog.isOpened}
        onCancel={handleCloseDialog}
        fields={dialogProps[dialog.type].fields}
        onConfirm={dialogProps[dialog.type].onConfirm}
        title={dialogProps[dialog.type].title}
        confirmButtonLabel={dialogProps[dialog.type].confirmButtonLabel}
      />
      <FormDialog
        isOpened={isRegisterNewUserDialogOpened}
        onCancel={handleCloseRegisterNewUserDialog}
        fields={[
          {
            name: "email_new_user",
            type: FIELD_TYPES.TEXT,
            label: "E-mail address",
            rules: string()
              .required()
              .matches(REGEX.EMAIL_ADDRESS, ERROR_MESSAGES.EMAIL_ADDRESS)
          },
          {
            name: "password",
            type: FIELD_TYPES.PASSWORD,
            label: "Password",
            autocomplete: "new-password",
            rules: string()
              .required()
              .matches(REGEX.PASSWORD, ERROR_MESSAGES.PASSWORD)
          },
          {
            name: "confirm_password",
            type: FIELD_TYPES.PASSWORD,
            label: "Confirm password",
            autocomplete: "new-password",
            rules: string().oneOf(
              [ref("password")],
              ERROR_MESSAGES.PASSWORDS_MUST_MATCH
            )
          },
          // {
          //   name: "creds_temporary",
          //   type: FIELD_TYPES.TOGGLE,
          //   defaultValue: true,
          //   label: "Change password on next login"
          // },
          {
            name: "divider",
            type: FIELD_TYPES.DIVIDER,
            label: "Personal Information"
          },
          {
            name: "first_name",
            type: FIELD_TYPES.TEXT,
            label: "First name",
            rules: string().required()
          },
          {
            name: "last_name",
            type: FIELD_TYPES.TEXT,
            label: "Last name",
            rules: string().required()
          },
          {
            name: "mobile",
            type: FIELD_TYPES.TEXT,
            label: "Mobile Number",
            rules: string()
          },
          {
            name: "divider2",
            type: FIELD_TYPES.DIVIDER,
            label: "Address"
          },
          {
            name: "street",
            type: FIELD_TYPES.TEXT,
            label: "Street",
            defaultValue: currentUser?.attributes?.["info:street"]?.[0] || "",
            rules: string()
          },
          {
            name: "locality",
            type: FIELD_TYPES.TEXT,
            defaultValue: currentUser?.attributes?.["info:locality"]?.[0] || "",
            label: "City or Locality",
            rules: string()
          },
          {
            name: "region",
            type: FIELD_TYPES.TEXT,
            defaultValue: currentUser?.attributes?.["info:region"]?.[0] || "",
            label: "State, Province, or Region",
            rules: string()
          },
          {
            name: "country",
            type: FIELD_TYPES.TEXT,
            defaultValue: currentUser?.attributes?.["info:country"]?.[0] || "",
            label: "Country",
            rules: string()
          },
          {
            name: "postal_code",
            type: FIELD_TYPES.TEXT,
            defaultValue:
              currentUser?.attributes?.["info:postal_code"]?.[0] || "",
            label: "Zip or Postal code",
            rules: string()
          },
          {
            name: "divider3",
            type: FIELD_TYPES.DIVIDER,
            label: "Company"
          },
          {
            name: "company_name",
            type: FIELD_TYPES.TEXT,
            label: "Company name",
            readOnly:
              currentUser?.attributes?.["info:companyName"] &&
              currentUser?.attributes?.["info:companyName"]?.[0] !== "",
            defaultValue:
              currentUser?.attributes?.["info:companyName"]?.[0] || "",
            rules: string()
          },
          {
            name: "tax_number",
            type: FIELD_TYPES.TEXT,
            readOnly:
              currentUser?.attributes?.["info:companyTaxNumber"] &&
              currentUser?.attributes?.["info:companyTaxNumber"]?.[0] !== "",
            defaultValue:
              currentUser?.attributes?.["info:companyTaxNumber"]?.[0] || "",
            label: "Company Tax number",
            rules: string()
          }
        ]}
        onConfirm={handleConfirmRegisterNewUser}
        title={"Register New User"}
        confirmButtonLabel={"Register"}
      />
    </>
  );
};
